<template>
  <ckeditor
    class="editor"
    :editor="ClassicEditor"
    :model-value="editorData"
    :config="editorConfig"
    @ready="handleReady"
    @destroy="handleDestroy"
    @input="handleUpdate"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { Editor as CKEditorInstance } from "ckeditor5";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
// import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";
// import { Essentials } from "@ckeditor/ckeditor5-essentials";
// import { Bold, Italic, Underline } from "@ckeditor/ckeditor5-basic-styles";
// import { Font } from "@ckeditor/ckeditor5-font";
// import { AutoLink, Link } from "@ckeditor/ckeditor5-link";
// import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
// import { Indent } from "@ckeditor/ckeditor5-indent";
// import { List } from "@ckeditor/ckeditor5-list";
// import { PasteFromOffice } from "@ckeditor/ckeditor5-paste-from-office";
// import { Table, TableToolbar } from "@ckeditor/ckeditor5-table";
// import { TextTransformation } from "@ckeditor/ckeditor5-typing";
// import { Alignment } from "@ckeditor/ckeditor5-alignment";
// import { Autoformat } from "@ckeditor/ckeditor5-autoformat";
// import { Mention } from "@ckeditor/ckeditor5-mention";
// import { Heading } from "@ckeditor/ckeditor5-heading";

import {
  ClassicEditor,
  Essentials,
  Bold,
  Italic,
  Underline,
  Font,
  AutoLink,
  Link,
  Indent,
  List,
  TextTransformation,
  Alignment,
  Autoformat,
  Mention,
  Heading,
  GeneralHtmlSupport,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageTextAlternative,
  ImageUpload,
  ImageInsert,
  ImageResize,
  AutoImage,
  PictureEditing,
  LinkImage,
  Base64UploadAdapter,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

// import { CloudServices } from "@ckeditor/ckeditor5-cloud-services";
// import {
//   AutoImage,
//   Image,
//   ImageCaption,
//   ImageInsert,
//   ImageResize,
//   ImageStyle,
//   ImageToolbar,
//   ImageUpload,
//   PictureEditing,
// } from "@ckeditor/ckeditor5-image";
// import { FileRepository, SimpleUploadAdapter } from "@ckeditor/ckeditor5-upload";
// import { CKBox, CKBoxImageEdit } from "@ckeditor/ckeditor5-ckbox";
const props = defineProps({
  editorData: {
    type: String,
    default: "",
  },
  feed: {
    type: Array,
    default: () => [],
  },
  minimumCharactersFeed: {
    type: Number,
    default: 1
  }
});

const emit = defineEmits<{
  "update:editorData": [newData: string];
  ready: [editor: CKEditorInstance];
  destroy: [];
}>();

const handleUpdate = (newData: string) => {
  emit("update:editorData", newData);
};

const handleReady = (editor: CKEditorInstance) => {
  emit("ready", editor);
};

const handleDestroy = () => {
  emit("destroy");
};

const editorConfig = computed(() => ({
  plugins: [
    Essentials,
    Bold,
    Italic,
    Font,
    Link,
    // Paragraph,
    Mention,
    Indent,
    List,
    // PasteFromOffice,
    // Table,
    // TableToolbar,
    TextTransformation,
    Alignment,
    Autoformat,
    GeneralHtmlSupport,
    Image,
    AutoImage,
    ImageCaption,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    ImageInsert,
    ImageResize,
    PictureEditing,
    AutoLink,
    LinkImage,
    Underline,
    Heading,
    Base64UploadAdapter,
    ImageTextAlternative,
    // FileRepository,
    // CloudServices,
    // SimpleUploadAdapter,
    // CKBox,
    // CKBoxImageEdit,
  ],
  toolbar: {
    items: [
      // "paragraph",
      "heading",
      "bold",
      "underline",
      "italic",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      // "undo",
      // "redo",
      // "indent",
      // "outdent",
      "alignment",
      "numberedList",
      "bulletedList",
      // "pasteFromOffice",
      // "table",
      // "autoformat",
      "|",
      "link",
      // "insertImage",
      // "imageUpload",
      // "attachDocument",
      // "customIcon",
      // "uploadImage",
      // "|",
      // "ckbox",
      // "ckboxImageEdit",
    ],
    shouldNotGroupWhenFull: true,
  },
  link: {
    addTargetToExternalLinks: true,
  },
  mention: {
    feeds: [
      {
        marker: "#",
        minimumCharacters: props.minimumCharactersFeed,
        feed: props.feed,
      },
    ],
  },
  htmlSupport: {
    allow: [
      {
        name: /.*/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
    disallow: [
      {
        name: /^(script|object|embed)$/i,
      },
      {
        name: /.*/,
        attributes: {
          key: /^on.*$/,
        },
      },
    ],
    htmlIframeSandbox: false,
  },
  heading: {
    options: [
      {
        model: "paragraph",
        name: "Párrafo",
        title: "Párrafo",
      },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
    ],
  },
}));
</script>

<style>
:root {
  /* Make the mention background blue. */
  --ck-color-mention-background: white;

  /* Make the mention text dark grey. */
  --ck-color-mention-text: #fa8c16;
}

.ck.ck-editor__main {
  max-height: min(calc(100vh - 400px), 600px);
  overflow-y: auto;
}

.editor .ck-content .ck-widget.html-object-embed {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  padding-top: 0 !important;
}

.editor .ck-content .ck-widget.html-object-embed::before {
  display: none !important;
  content: none !important;
}

.editor .ck-content .html-object-embed::before,
.editor .ck-content .html-object-embed:before,
.editor .ck-content .html-object-embed::after,
.editor .ck-content .html-object-embed:after,
.editor .ck-content [data-html-object-embed-label]::before,
.editor .ck-content [data-html-object-embed-label]:before,
.editor .ck-content [data-html-object-embed-label]::after,
.editor .ck-content [data-html-object-embed-label]:after {
  display: none !important;
  content: none !important;
}

.editor .ck-content .html-object-embed .ck-widget__label,
.editor .ck-content [data-html-object-embed-label] .ck-widget__label {
  display: none !important;
}

.ck-widget.html-object-embed,
.ck-widget.html-object-embed[data-html-object-embed-label] {
  padding-top: 0 !important;
}

.ck-widget.html-object-embed::before,
.ck-widget.html-object-embed:before,
.ck-widget.html-object-embed[data-html-object-embed-label]::before,
.ck-widget.html-object-embed[data-html-object-embed-label]:before {
  display: none !important;
  content: none !important;
}

.editor .ck-content .ck-widget.html-object-embed .html-object-embed__content {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible;
}

.editor .ck-content .ck-widget.html-object-embed iframe {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  border: 0;
  display: block;
}

.ck-content .image {
  width: auto ;
}
</style>